import "@/App.css";
import { useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { initAnalytics, trackPageView } from "./lib/analytics";
import { setPageMeta, ROUTE_META } from "./lib/seo";
import { MobileCartBar } from "./components/MobileCartBar";
import { MobileBottomNav } from "./components/MobileBottomNav";
import Home from "./pages/Home";
import Questionnaire from "./pages/Questionnaire";
import Recommendations from "./pages/Recommendations";
import RecipeDetail from "./pages/RecipeDetail";
import HerbEncyclopedia from "./pages/HerbEncyclopedia";
import HerbDetail from "./pages/HerbDetail";
import DailyNews from "./pages/DailyNews";
import InfusoShop from "./pages/InfusoShop";
import MensScorecard from "./pages/MensScorecard";
import WomensAssessment from "./pages/WomensAssessment";
import MensProtocol from "./pages/MensProtocol";
import WomensProtocol from "./pages/WomensProtocol";
import MensVitalityGuide from "./pages/MensVitalityGuide";
import MensMaintenanceGuide from "./pages/MensMaintenanceGuide";
import WomensHormoneGuide from "./pages/WomensHormoneGuide";
import WomensBalanceGuide from "./pages/WomensBalanceGuide";
import BillingSuccess from "./pages/BillingSuccess";
import BillingCancel from "./pages/BillingCancel";
import Account from "./pages/Account";
import Storefront from "./pages/Storefront";
import Cart from "./pages/Cart";
import StoreSuccess from "./pages/StoreSuccess";
import FreeAssessmentLanding from "./pages/FreeAssessmentLanding";
const PitchDeck = lazy(() => import("./pages/PitchDeck"));
const Admin = lazy(() => import("./pages/Admin"));
const GrowthOS = lazy(() => import("./pages/GrowthOS"));
const HerbGuide = lazy(() => import("./pages/HerbGuide"));
const HerbComparison = lazy(() => import("./pages/HerbComparison"));
const WhiteLabel = lazy(() => import("./pages/WhiteLabel"));
const WhiteLabelPricing = lazy(() => import("./pages/WhiteLabelPricing"));
const WhiteLabelHowItWorks = lazy(() => import("./pages/WhiteLabelHowItWorks"));
const WhiteLabelFaq = lazy(() => import("./pages/WhiteLabelFaq"));
const WLSignup = lazy(() => import("./pages/wl/WLSignup"));
const WLLogin = lazy(() => import("./pages/wl/WLLogin"));
const WLVerify = lazy(() => import("./pages/wl/WLVerify"));
const WLReset = lazy(() => import("./pages/wl/WLReset"));
const WLOnboarding = lazy(() => import("./pages/wl/WLOnboarding"));
const WLDashboard = lazy(() => import("./pages/wl/WLDashboard"));
const WLStatus = lazy(() => import("./pages/wl/WLStatus"));
const WLPreviewPage = lazy(() => import("./pages/wl/WLPreviewPage"));
const WLAccount = lazy(() => import("./pages/wl/WLAccount"));
const WLLegalDocs = lazy(() => import("./pages/wl/WLLegalDocs"));
const WLJoin = lazy(() => import("./pages/wl/WLJoin"));
const WLStoreAssessment = lazy(() => import("./pages/wl/WLStoreAssessment"));
const WLAdmin = lazy(() => import("./pages/wl/WLAdmin"));
const WLPilot = lazy(() => import("./pages/wl/WLPilot"));
const LegalPage = lazy(() => import("./pages/Legal"));
import { CookieConsent } from "./components/CookieConsent";
import Header from "./components/Header";
import AuthCallback from "./components/AuthCallback";
import { AuthProvider } from "./context/AuthContext";
import { CartProvider } from "./context/CartContext";
import { AdminAuthProvider } from "./context/AdminAuthContext";
import { WLAuthProvider } from "./context/WLAuthContext";
import AuthModal from "./components/AuthModal";
import AgreementModal from "./components/AgreementModal";
import PaywallModal from "./components/PaywallModal";
import ProtectedRoute from "./components/ProtectedRoute";
import PremiumRoute from "./components/PremiumRoute";
import PremiumWellnessGate from "./components/PremiumWellnessGate";

function AppContent() {
  const location = useLocation();
  useEffect(() => { initAnalytics(); }, []);
  useEffect(() => {
    const meta = ROUTE_META[location.pathname];
    if (meta || !(location.pathname.startsWith("/herbs/") || location.pathname.startsWith("/guides/") || location.pathname.startsWith("/compare/"))) {
      setPageMeta(meta?.title, meta?.desc);
    }
    trackPageView(location.pathname);
  }, [location.pathname]);
  // Google OAuth return: process #session_id before any route/auth check
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <>
      {location.pathname !== "/free-assessment" && !location.pathname.startsWith("/a/") && location.pathname !== "/admin/white-label" && <Header />}
      <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/free-assessment" element={<FreeAssessmentLanding />} />
            {["privacy", "terms", "disclosure", "returns", "medical-disclaimer", "contact", "about", "faq"].map((slug) => (
              <Route key={slug} path={`/${slug}`} element={<Suspense fallback={null}><LegalPage page={slug} /></Suspense>} />
            ))}
            <Route
              path="/questionnaire"
              element={<Questionnaire />}
            />
            <Route
              path="/recommendations"
              element={<Recommendations />}
            />
            <Route path="/recipe/:recipeId" element={<RecipeDetail />} />
            <Route path="/herbs" element={<HerbEncyclopedia />} />
            <Route path="/guides/:slug" element={<Suspense fallback={null}><HerbGuide /></Suspense>} />
            <Route path="/compare/:slug" element={<Suspense fallback={null}><HerbComparison /></Suspense>} />
            <Route path="/white-label" element={<Suspense fallback={null}><WhiteLabel /></Suspense>} />
            <Route path="/white-label/pricing" element={<Suspense fallback={null}><WhiteLabelPricing /></Suspense>} />
            <Route path="/white-label/how-it-works" element={<Suspense fallback={null}><WhiteLabelHowItWorks /></Suspense>} />
            <Route path="/white-label/faq" element={<Suspense fallback={null}><WhiteLabelFaq /></Suspense>} />
            <Route path="/white-label/signup" element={<Suspense fallback={null}><WLSignup /></Suspense>} />
            <Route path="/white-label/login" element={<Suspense fallback={null}><WLLogin /></Suspense>} />
            <Route path="/white-label/verify" element={<Suspense fallback={null}><WLVerify /></Suspense>} />
            <Route path="/white-label/reset" element={<Suspense fallback={null}><WLReset /></Suspense>} />
            <Route path="/white-label/onboarding" element={<Suspense fallback={null}><WLOnboarding /></Suspense>} />
            <Route path="/white-label/dashboard" element={<Suspense fallback={null}><WLDashboard /></Suspense>} />
            <Route path="/white-label/status" element={<Suspense fallback={null}><WLStatus /></Suspense>} />
            <Route path="/white-label/preview" element={<Suspense fallback={null}><WLPreviewPage /></Suspense>} />
            <Route path="/white-label/account" element={<Suspense fallback={null}><WLAccount /></Suspense>} />
            <Route path="/white-label/legal" element={<Suspense fallback={null}><WLLegalDocs /></Suspense>} />
            <Route path="/white-label/join" element={<Suspense fallback={null}><WLJoin /></Suspense>} />
            <Route path="/white-label/pilot" element={<Suspense fallback={null}><WLPilot /></Suspense>} />
            <Route path="/a/:key" element={<Suspense fallback={null}><WLStoreAssessment /></Suspense>} />
            <Route path="/admin/white-label" element={<Suspense fallback={null}><WLAdmin /></Suspense>} />
            <Route path="/herbs/:herbId" element={<HerbDetail />} />
            <Route path="/news" element={<DailyNews />} />
            <Route path="/daily-news" element={<Navigate to="/news" replace />} />
            <Route path="/shop" element={<InfusoShop />} />
            <Route path="/store" element={<Storefront />} />
            <Route path="/cart" element={<Cart />} />
            <Route path="/store/success" element={<StoreSuccess />} />

            {/* Premium gated assessments */}
            <Route
              path="/mens-scorecard"
              element={<PremiumRoute><MensScorecard /></PremiumRoute>}
            />
            <Route
              path="/womens-assessment"
              element={<PremiumRoute><WomensAssessment /></PremiumRoute>}
            />

            <Route path="/mens-vitality-protocol" element={<MensProtocol />} />
            <Route path="/womens-balance-protocol" element={<WomensProtocol />} />
            <Route path="/mens-vitality-guide" element={<PremiumWellnessGate><MensVitalityGuide /></PremiumWellnessGate>} />
            <Route path="/mens-maintenance-guide" element={<MensMaintenanceGuide />} />
            <Route path="/womens-hormone-guide" element={<PremiumWellnessGate><WomensHormoneGuide /></PremiumWellnessGate>} />
            <Route path="/womens-balance-guide" element={<WomensBalanceGuide />} />

            <Route path="/billing/success" element={<BillingSuccess />} />
            <Route path="/billing/cancel" element={<BillingCancel />} />
            <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
            <Route path="/pitch" element={<Suspense fallback={null}><PitchDeck /></Suspense>} />
            <Route path="/admin" element={<Suspense fallback={null}><Admin /></Suspense>} />
            <Route path="/admin/growth" element={<Suspense fallback={null}><GrowthOS /></Suspense>} />
          </Routes>
          <AuthModal />
          <AgreementModal />
          <PaywallModal />
          <MobileCartBar />
          {location.pathname !== "/free-assessment" && !location.pathname.startsWith("/a/") && location.pathname !== "/admin/white-label" && <MobileBottomNav />}
          <CookieConsent />
    </>
  );
}

function App() {
  return (
    <div className="App min-h-screen bg-white pb-[60px] md:pb-0">
      <BrowserRouter>
        <AuthProvider>
          <CartProvider>
          <AdminAuthProvider>
          <WLAuthProvider>
          <AppContent />
          <Toaster position="top-center" richColors />
          </WLAuthProvider>
          </AdminAuthProvider>
          </CartProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
